import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Link, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import LikedProduct from '../components/LikedProduct';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
const Likes = () => {
  const { user, api } = useAuth();
  const [likedProducts, setLikedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullTotal, setShowFullTotal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    productId: null,
  });

  // Group products by brand
  const groupedProducts = likedProducts.reduce((groups, product) => {
    const brandName = product.brand?.name || 'Other';
    if (!groups[brandName]) {
      groups[brandName] = [];
    }
    groups[brandName].push(product);
    return groups;
  }, {});

  // Sort brands alphabetically
  const sortedBrands = Object.keys(groupedProducts).sort();

  useEffect(() => {
    const fetchLikedProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/likes');
        setLikedProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching liked products:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchLikedProducts();
    }
  }, [user, api]);

  const handleRemoveLike = async (productId) => {
    setDeleteDialog({
      open: true,
      productId,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/likes/${deleteDialog.productId}`);
      setLikedProducts(prevProducts => 
        prevProducts.filter(product => product.id !== deleteDialog.productId)
      );
    } catch (error) {
      console.error('Error removing product from likes:', error);
    } finally {
      setDeleteDialog({ open: false, productId: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, productId: null });
  };

  // Handle scroll for total box visibility
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      setShowFullTotal(documentHeight - (scrollTop + windowHeight) < 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const totalAmount = likedProducts.reduce((sum, product) => sum + (product.price || 0), 0);

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Liked Items
        </Typography>
        {likedProducts.length > 0 && (
          <Typography variant="subtitle1" color="text.secondary">
            {likedProducts.length} items
          </Typography>
        )}
      </Box>

      {likedProducts.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <Box sx={{ 
            position: 'relative',
            width: 96,
            height: 96,
            borderRadius: '50%',
            bgcolor: 'primary.brandLight',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}>
            <FavoriteBorderIcon sx={{ 
              fontSize: 32,
              color: 'primary.main'
            }} />
            <Box sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography sx={{ 
                color: 'white', 
                fontSize: 12, 
                fontWeight: 600,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                mt: '1px'
              }}>
                0
              </Typography>
            </Box>
          </Box>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            No likes yet
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" textAlign="center">
            Explore styles now, and click on the like button to get back to them later.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {sortedBrands.map((brandName) => (
            <Box key={brandName} id={`brand-${brandName}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{brandName}</Typography>
              </Box>
              <Stack spacing={2}>
                {groupedProducts[brandName].map((product) => (
                  <LikedProduct
                    key={product.id}
                    id={product.id}
                    image={product.media?.images?.[0]?.url || product.image_url || product.imageUrl}
                    brand={product.brand?.name || product.brandName}
                    name={product.title || product.name}
                    price={product.price}
                    originalPrice={product.product_details?.original_price || product.original_price}
                    onRemove={() => handleRemoveLike(product.id)}
                    product={product}
                  />
                ))}
              </Stack>
              {brandName !== sortedBrands[sortedBrands.length - 1] && (
                <Divider sx={{ my: 3 }} />
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Remove Item from Likes?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this item from your liked items?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Total Box - Split into two parts */}
      {likedProducts.length > 0 && (
        <>
          {/* Fixed Total - Always visible */}
          <Box 
            sx={{ 
              position: 'fixed',
              bottom: showFullTotal ? 200 : 56,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              py: 2,
              px: 3,
              borderTop: 1,
              borderColor: 'divider',
              maxWidth: 600,
              mx: 'auto',
              zIndex: 1001,
              transition: 'bottom 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Estimated Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ${totalAmount.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Additional Info - Shows when scrolled to bottom */}
          <Box 
            sx={{ 
              position: 'fixed',
              bottom: 56,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              maxWidth: 600,
              mx: 'auto',
              zIndex: 1000,
              transform: `translateY(${showFullTotal ? '0' : '100%'})`,
              transition: 'transform 0.3s ease',
              borderTop: 1,
              borderColor: 'divider',
              py: 3,
              px: 3,
            }}
          >
            <Typography 
              variant="body4" 
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              To proceed and complete your order, you'd need to order each item separately. We're working hard to automate this step. Please share your feedback and any information you'd like us to know{' '}
              <Link
                href="/feedback"
                sx={{
                  color: 'success.main',
                  fontStyle: 'italic',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                here
              </Link>
            </Typography>
          </Box>
        </>
      )}
      
      <Box sx={{ mb: likedProducts.length > 0 ? (showFullTotal ? 40 : 40) : 0 }} />
    </Box>
  );
};

export default Likes;